import React, { useEffect, useState } from "react";
import Select from "react-select";
import dayjs from "dayjs";

const FilterBar = ({ employees, onFilterChange, showFrequencyFilter, defaultRangeType = "last7", onRefresh, loading }) => {
  const today = dayjs().format("YYYY-MM-DD");

  const getInitialDates = (type) => {
    if (type === "today") return [today, today];
    if (type === "last30") return [dayjs().subtract(29, "day").format("YYYY-MM-DD"), today];
    if (type === "last6m") return [dayjs().subtract(6, "month").format("YYYY-MM-DD"), today];
    if (type === "last1y") return [dayjs().subtract(1, "year").format("YYYY-MM-DD"), today];
    return [dayjs().subtract(6, "day").format("YYYY-MM-DD"), today];
  };

  const [initialStart, initialEnd] = getInitialDates(defaultRangeType);

  const [rangeType, setRangeType] = useState(defaultRangeType);
  const [startDate, setStartDate] = useState(initialStart);
  const [endDate, setEndDate] = useState(initialEnd);
  const [appliedStartDate, setAppliedStartDate] = useState(initialStart);
  const [appliedEndDate, setAppliedEndDate] = useState(initialEnd);
  const [frequency, setFrequency] = useState("daily");
  const [employeeId, setEmployeeId] = useState("");
  const [employeeIds, setEmployeeIds] = useState([]); // for multi-select (if needed)

  const employeeOptions = employees.map((emp) => ({
    value: emp.id,
    label: emp.fullName,
  }));
  // 🔹 Update dates automatically when preset changes
  useEffect(() => {
    let nextStart = startDate;
    let nextEnd = endDate;

    if (rangeType === "today") {
      nextStart = today;
      nextEnd = today;
    } else if (rangeType === "last7") {
      nextStart = dayjs().subtract(6, "day").format("YYYY-MM-DD");
      nextEnd = today;
    } else if (rangeType === "last30") {
      nextStart = dayjs().subtract(29, "day").format("YYYY-MM-DD");
      nextEnd = today;
    } else if (rangeType === "last6m") {
      nextStart = dayjs().subtract(6, "month").format("YYYY-MM-DD");
      nextEnd = today;
    } else if (rangeType === "last1y") {
      nextStart = dayjs().subtract(1, "year").format("YYYY-MM-DD");
      nextEnd = today;
    }

    if (rangeType !== "custom") {
      setStartDate(nextStart);
      setEndDate(nextEnd);
      setAppliedStartDate(nextStart);
      setAppliedEndDate(nextEnd);
    }
    // If "custom", do nothing — let user pick manually
  }, [rangeType]);

  // 🔹 Notify parent
  useEffect(() => {
    onFilterChange({
      startDate: appliedStartDate,
      endDate: appliedEndDate,
      frequency,
      employeeId,
      employeeIds,
    });
  }, [
    appliedStartDate,
    appliedEndDate,
    frequency,
    employeeId,
    employeeIds,
    onFilterChange,
  ]);

  const handleApplyCustomRange = () => {
    if (!startDate || !endDate) return;
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  return (
    <div className="flex flex-wrap gap-4 bg-[#1a1a1a] p-4 rounded-md mb-6">
      {/* Preset Dropdown */}
      <div>
        <select
          value={rangeType}
          onChange={(e) => setRangeType(e.target.value)}
          className="bg-[#0c0b0b] text-white px-2 py-1 border border-gray-600 rounded"
        >
          <option value="today">Today</option>
          <option value="last7">Last 7 Days</option>
          <option value="last30">Last 30 Days</option>
          <option value="last6m">Last 6 Months</option>
          <option value="last1y">Last 1 Year</option>
          <option value="custom">Custom Range</option>
        </select>
      </div>

      {/* Custom Date Range (only visible if "custom" selected) */}
      {rangeType === "custom" && (
        <div className="flex items-center space-x-2">
          <label className="text-sm text-gray-700">From</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="bg-white text-gray-800 px-2 py-1 border border-gray-300 rounded"
          />
          <label className="text-sm text-gray-700">To</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="bg-white text-gray-800 px-2 py-1 border border-gray-300 rounded"
          />
          <button
            type="button"
            onClick={handleApplyCustomRange}
            disabled={!startDate || !endDate}
            className="px-3 py-1.5 rounded bg-[#FBB033] text-black font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            Apply
          </button>
        </div>
      )}

      {/* Frequency */}
      {showFrequencyFilter && (
        <div>
          <select
            value={frequency}
            onChange={(e) => setFrequency(e.target.value)}
            className="bg-[#0c0b0b] text-white px-2 py-1 border border-gray-600 rounded"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      )}

      {/* Employee */}
      <Select
        isMulti
        options={employeeOptions}
        value={employeeOptions.filter((opt) => employeeIds.includes(opt.value))}
        onChange={(selected) =>
          setEmployeeIds(selected ? selected.map((s) => s.value) : [])
        }
        styles={{
          control: (base) => ({
            ...base,
            backgroundColor: "#0c0b0b",
            borderColor: "#4B5563",
            color: "white",
          }),
          menu: (base) => ({
            ...base,
            backgroundColor: "#1a1a1a",
            color: "white",
          }),
          multiValue: (base) => ({
            ...base,
            backgroundColor: "#FBB033",
          }),
        }}
      />
      {onRefresh && (
        <button
          onClick={onRefresh}
          disabled={loading}
          className="font-be-jost-pro font-medium text-sm border border-[#FBB033] text-[#FBB033] rounded-md px-5 py-2 hover:bg-[#FBB033] hover:text-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      )}
    </div>
  );
};

export default FilterBar;
